import { ViewportScroller } from "@angular/common";
import { Component, type OnInit, inject } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { environment } from "../../../environments/environment";
import { AccountService, type IUser, UserService } from "../../core";
import { type Color, ColorService } from "../../shared/color/color.service";
import { PHONE_MASK_US } from "../../shared/constants/input.constants";
import { SetupTwoFactorModalComponent } from "../components/setup-two-factor-modal/setup-two-factor-modal.component";

@Component({
    selector: "app-settings",
    templateUrl: "./settings.component.html",
    standalone: false,
})
export class SettingsComponent implements OnInit {
    private viewPortScroller = inject(ViewportScroller);
    private accountService = inject(AccountService);
    colorService = inject(ColorService);
    private userService = inject(UserService);
    private modalService = inject(NgbModal);
    private fb = inject(UntypedFormBuilder);

    environment = environment;
    phoneMask = PHONE_MASK_US;

    colorPickerEnabled: boolean;
    hasClientAccessRequestsAuthority = false;
    showClientMessages = false;

    myClientsTooltipText: string;
    clientActionTooltipText: string;
    clientMessageTooltipText: string;
    clientUploadDocumentTooltipText: string;

    currentAccount: IUser;
    error: string;
    success: string;
    settingsForm = this.fb.group({
        firstName: [
            undefined,
            [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(50),
            ],
        ],
        lastName: [
            undefined,
            [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(50),
            ],
        ],
        email: [
            undefined,
            [
                Validators.required,
                Validators.minLength(5),
                Validators.maxLength(254),
                Validators.email,
            ],
        ],
        phoneNumber: [undefined, [Validators.required]],
        avatarColor: [],
        costInCents: [undefined, [Validators.required]],
        activated: [false],
        authorities: [[]],
        langKey: ["en"],
        login: [],
        imageUrl: [],
        notificationByEmail: [],
        notificationByApp: [],
        notificationProjectChanges: [],
        notificationTaskChanges: [],
        notificationProjectComments: [],
        notificationClientActions: ["NONE", Validators.required],
        notificationProjectDocumentUploaded: [],
        notificationProjectClientMessage: [],
        notificationProjectClientActionCompleted: [],
        notificationOpenProjects: [],
        notificationDocument: ["NONE", Validators.required],
        notificationClientMessage: ["NONE", Validators.required],
        notificationClientAccessRequests: [],
        notificationClientMessageAssignedToUser: [],
        notificationPaymentReceived: [],
    });

    ngOnInit(): void {
        this.loadAccount(true);
    }

    loadAccount(force: boolean): void {
        this.accountService.identity(force).then((account) => {
            this.currentAccount = account;
            if (
                this.currentAccount.phoneNumber &&
                this.currentAccount.phoneNumber.indexOf("+") >= 0
            ) {
                this.phoneMask = null;
            }
            this.hasClientAccessRequestsAuthority =
                this.accountService.hasAnyAuthority([
                    "ROLE_MANAGE_CLIENT_PORTAL_ACCESS",
                ]);

            if (
                this.currentAccount.firmSettings.enableClientMessages &&
                this.accountService.hasAnyAuthority(["ROLE_CLIENT_MESSAGES"])
            ) {
                this.showClientMessages = true;
            }

            this.updateForm(account);
            this.buildTooltipText();
        });
    }

    selectColor(c: Color): void {
        this.settingsForm.patchValue({ avatarColor: c.hex });
        this.colorPickerEnabled = false;
    }

    buildTooltipText(): void {
        const hideManagerField =
            this.currentAccount.firmSettings.hideClientManagerField;
        const hideBookKeeperField =
            this.currentAccount.firmSettings.hideClientBookkeeperField;

        this.myClientsTooltipText = `Get notified if you are the Partner In Charge ${!hideManagerField ? "or Manager" : ""}
        ${!hideBookKeeperField ? "or Bookkeeper" : ""} for the client`;

        this.clientActionTooltipText = `Get an email notification when the client completes any action item. Select either "My Clients" -
      the clients you are the Partner in Charge ${!hideManagerField ? "or Manager" : ""} ${!hideBookKeeperField ? "or Bookkeeper" : ""} for,
      "All Clients", or "None" if you don't want to receive this notification.`;

        this.clientMessageTooltipText = `Get an email notification when a client sends a message. Select either “My Clients” -
      the clients you are the Partner in Charge ${!hideManagerField ? "or Manager" : ""} ${!hideBookKeeperField ? "or Bookkeeper" : ""} for,
      or “All Clients”, or “None” if you dont want to receive this notification`;

        this.clientUploadDocumentTooltipText = `Get an email notification when a client uploads a document. Select either “My Clients” -
      the clients you are the Partner in Charge ${!hideManagerField ? "or Manager" : ""} ${!hideBookKeeperField ? "or Bookkeeper" : ""} for,
      or “All Clients”, or “None” if you dont want to receive this notification`;
    }

    save(): void {
        const settingsAccount = this.accountFromForm();
        this.accountService.save(settingsAccount).subscribe(
            () => {
                this.viewPortScroller.scrollToPosition([0, 0]);
                this.error = null;
                this.success = "OK";
                this.accountService.identity(true).then((account) => {
                    this.updateForm(account);
                });
            },
            () => {
                this.success = null;
                this.error = "ERROR";
            },
        );
    }

    updateForm(account: IUser): void {
        this.settingsForm.patchValue({
            firstName: account.firstName,
            lastName: account.lastName,
            email: account.email,
            phoneNumber: account.phoneNumber,
            avatarColor: account.avatarColor,
            activated: account.activated,
            authorities: account.authorities,
            langKey: account.langKey,
            login: account.login,
            imageUrl: account.imageUrl,
            costInCents: account.costInCents,
            notificationByEmail: account.notificationByEmail,
            notificationByApp: account.notificationByApp,
            notificationProjectChanges: account.notificationProjectChanges,
            notificationTaskChanges: account.notificationTaskChanges,
            notificationProjectComments: account.notificationProjectComments,
            notificationClientActions:
                account.notificationClientActions || "MY_CLIENTS",
            notificationProjectDocumentUploaded:
                account.notificationProjectDocumentUploaded,
            notificationProjectClientMessage:
                account.notificationProjectClientMessage,
            notificationProjectClientActionCompleted:
                account.notificationProjectClientActionCompleted,
            notificationOpenProjects: account.notificationOpenProjects,
            notificationDocument: account.notificationDocument || "MY_CLIENTS",
            notificationClientMessage:
                account.notificationClientMessage || "MY_CLIENTS",
            notificationClientAccessRequests:
                account.notificationClientAccessRequests,
            notificationClientMessageAssignedToUser:
                account.notificationClientMessageAssignedToUser,
            notificationPaymentReceived:
                account.notificationPaymentReceived || "NONE",
        });
    }

    openSetupMFAModal(): void {
        this.modalService.open(SetupTwoFactorModalComponent).result.then(
            (mfaTypeActived: "GA" | "SMS") => {
                this.currentAccount.mfaEnabled = true;
                if (mfaTypeActived === "GA") {
                    this.currentAccount.mfaGaEnabled = true;
                } else if (mfaTypeActived === "SMS") {
                    this.currentAccount.mfaSmsEnabled = true;
                }
            },
            () => {},
        );
    }

    resetMFA(): void {
        if (!confirm("Are you sure you want to reset your MFA status?")) {
            return;
        }
        this.userService.resetMFA(this.currentAccount.id).subscribe(() => {
            this.currentAccount.mfaEnabled = false;
            this.currentAccount.mfaGaEnabled = false;
            this.currentAccount.mfaSmsEnabled = false;
        });
    }

    onKeydown(event: KeyboardEvent): void {
        if (
            event.code === "NumpadAdd" ||
            (event.code === "Equal" && event.shiftKey)
        ) {
            this.phoneMask = null;
        }
    }

    private accountFromForm(): IUser {
        return {
            firstName: this.settingsForm.get("firstName").value,
            lastName: this.settingsForm.get("lastName").value,
            email: this.settingsForm.get("email").value,
            phoneNumber: this.settingsForm.get("phoneNumber").value,
            avatarColor: this.settingsForm.get("avatarColor").value,
            activated: this.settingsForm.get("activated").value,
            authorities: this.settingsForm.get("authorities").value,
            langKey: this.settingsForm.get("langKey").value,
            login: this.settingsForm.get("login").value,
            imageUrl: this.settingsForm.get("imageUrl").value,
            costInCents: this.settingsForm.get("costInCents").value,
            notificationByEmail: this.settingsForm.get("notificationByEmail")
                .value,
            notificationByApp: this.settingsForm.get("notificationByApp").value,
            notificationProjectChanges: this.settingsForm.get(
                "notificationProjectChanges",
            ).value,
            notificationTaskChanges: this.settingsForm.get(
                "notificationTaskChanges",
            ).value,
            notificationProjectComments: this.settingsForm.get(
                "notificationProjectComments",
            ).value,
            notificationClientActions: this.settingsForm.get(
                "notificationClientActions",
            ).value,
            notificationProjectDocumentUploaded: this.settingsForm.get(
                "notificationProjectDocumentUploaded",
            ).value,
            notificationProjectClientMessage: this.settingsForm.get(
                "notificationProjectClientMessage",
            ).value,
            notificationProjectClientActionCompleted: this.settingsForm.get(
                "notificationProjectClientActionCompleted",
            ).value,
            notificationOpenProjects: this.settingsForm.get(
                "notificationOpenProjects",
            ).value,
            notificationDocument: this.settingsForm.get("notificationDocument")
                .value,
            notificationClientMessage: this.settingsForm.get(
                "notificationClientMessage",
            ).value,
            notificationClientAccessRequests: this.settingsForm.get(
                "notificationClientAccessRequests",
            ).value,
            notificationClientMessageAssignedToUser: this.settingsForm.get(
                "notificationClientMessageAssignedToUser",
            ).value,
            notificationPaymentReceived: this.settingsForm.get(
                "notificationPaymentReceived",
            ).value,
        };
    }
}
